/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import template from './query-route-page.component.html';
import {OBJECT_TYPE} from '../../common/constants/object-type';
import {QueryHelperService} from '../../common/controller/query-helper.service';
import {formatAdnStat} from '../../common/filters/format-adn-stat';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {WINDOW} from '../../angular-platform-providers';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';

export interface QueryRouteStateData {
  title?: string;
  childType?: string;
  parentType?: string;
  superParentType?: string;
  useAsyncStats?: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-query-route-page',
  template
})
export class QueryRoutePageComponent implements OnInit, OnDestroy {
  ctrl: any = {};
  chartType = '';

  readonly pageSize = 1000;
  currentPage = 1;

  private cleanupQuery: (() => void) | null = null;
  private lastInitKey = '';
  private deregisterTransitions: () => void;

  constructor(
    private readonly adnQueryHelper: QueryHelperService,
    private readonly $state: StateService,
    private readonly uiRouter: UIRouter,
    private readonly translate: TranslateService,
    @Inject(WINDOW) private readonly window: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly appLocale: AppLocaleService
  ) {
  }

  ngOnInit(): void {
    this.initializeQuery();
    this.deregisterTransitions = this.uiRouter.transitionService.onSuccess(
      {to: (state: any) => !!state.data?.childType},
      () => this.initializeQuery()
    ) as () => void;
  }

  ngOnDestroy(): void {
    if (this.deregisterTransitions) {
      this.deregisterTransitions();
      this.deregisterTransitions = null;
    }
    this.destroyQuery();
  }

  get hasResults(): boolean {
    return !!this.ctrl?.rows?.length;
  }

  get hasGroupedResults(): boolean {
    return this.hasResults && !!this.ctrl.rows[0]?.chunks;
  }

  get hasFlatResults(): boolean {
    return this.hasResults && !this.ctrl.rows[0]?.chunks;
  }

  get totalRows(): number {
    return this.ctrl?.rows?.length || 0;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRows / this.pageSize));
  }

  get pageRangeStart(): number {
    return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageRangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRows);
  }

  get pageNumbers(): number[] {
    return visiblePageNumbers(this.currentPage, this.totalPages);
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
  }

  resetPagination(): void {
    this.currentPage = 1;
  }

  onDownloadCsv(): void {
    if (!_.isFunction(this.ctrl?.saveDownloadFields) || !_.isFunction(this.ctrl?.getRows)) {
      return;
    }

    this.ctrl.saveDownloadFields();

    const rows = this.ctrl.getRows() || [];
    const header = (this.ctrl.downloadFields || []).map((value: any) => this.escapeCsvValue(value)).join(',');
    const body = rows.map((row: any) => {
      return (this.ctrl.downloadColumnOrder || []).map((column: string) => {
        return this.escapeCsvValue(row[column]);
      }).join(',');
    }).join('\r\n');
    const csv = '\uFEFF' + header + '\r\n' + body;
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = this.window.URL.createObjectURL(blob);
    const link = this.window.document.createElement('a');
    link.href = url;
    link.download = this.ctrl.csvTableTitle || 'query-results.csv';
    this.window.document.body.appendChild(link);
    link.click();
    this.window.document.body.removeChild(link);
    this.window.URL.revokeObjectURL(url);
  }

  groupRows(): any[] {
    return this.paginate(this.sortRows(this.ctrl?.rows, this.ctrl?.sorting?.groupSortValue));
  }

  groupChunks(row: any): any[] {
    return this.sortRows(row?.chunks, this.ctrl?.sorting?.rowSortValue);
  }

  flatRows(): any[] {
    return this.paginate(this.sortRows(this.ctrl?.rows, this.ctrl?.sorting?.rowSortValue));
  }

  sortField(field: any): void {
    this.ctrl?.sorting?.makeSortQuery?.(field);
    this.resetPagination();
  }

  fieldKey(field: any): string {
    return field?.apiId || field?.altId || field?.id;
  }

  fieldHeaderText(field: any): string {
    if (field?.abbr && !this.ctrl?.sel?.fullText) {
      return field.abbr;
    }
    return field?.text || '';
  }

  fieldHeaderTitle(field: any): string {
    return field?.text || '';
  }

  fieldSortClasses(field: any): Record<string, boolean> {
    const selected = this.isFieldSelected(field);
    return {
      'fa-sort': !selected,
      'fa-sort-asc': selected && !!this.ctrl?.sorting?.asc,
      'fa-sort-desc': selected && !this.ctrl?.sorting?.asc
    };
  }

  isFieldSelected(field: any): boolean {
    return this.ctrl?.sorting?.orderByParam === this.fieldKey(field)
      && this.ctrl?.sorting?.orderByExtra === field?.extra;
  }

  formatFieldValue(item: any, field: any): string {
    return formatAdnStat(
      item?.[this.fieldKey(field)],
      this.fieldKey(field),
      null,
      field?.extra,
      this.LocalNetworkProfile.get('defaultCurrency'),
      this.appLocale.getLocaleId()
    );
  }

  formatTotalValue(item: any, field: any): string {
    return formatAdnStat(
      item?.[this.fieldKey(field)],
      this.fieldKey(field),
      null,
      field?.extra,
      this.LocalNetworkProfile.get('defaultCurrency'),
      this.appLocale.getLocaleId()
    );
  }

  fieldDateValue(item: any, field: any): any {
    return item?.[field?.parentId]?.[this.fieldKey(field)];
  }

  getGroupingEntity(item: any, grouping: any): any {
    return item?.[grouping?.apiParam] || {};
  }

  getSubGroupingEntity(item: any): any {
    const grouping = this.ctrl?.withGrouping2;
    return item?.[grouping?.queryStatParam || grouping?.apiParam] || {};
  }

  groupRowHasLink(item: any): boolean {
    const entity = this.getGroupingEntity(item, this.ctrl?.withGrouping);
    return !!entity?.url && !!this.ctrl?.withGrouping?.stateName;
  }

  groupRowHasName(item: any): boolean {
    return !!this.getGroupingEntity(item, this.ctrl?.withGrouping)?.name;
  }

  flatRowHasLink(item: any): boolean {
    const grouping = this.ctrl?.withGrouping;
    if (!grouping || grouping === 'TARGETING' || grouping.apiParam === 'chunk') {
      return false;
    }
    const entity = item?.[grouping.queryStatParam || grouping.apiParam];
    return !!entity?.id;
  }

  flatRowStateName(): string {
    return this.ctrl?.withGrouping?.queryStatUrlParam || this.ctrl?.withGrouping?.stateName || '';
  }

  flatRowParams(item: any): any {
    const grouping = this.ctrl?.withGrouping;
    const entity = item?.[grouping?.queryStatParam || grouping?.apiParam];
    return {id: entity?.id};
  }

  rowName(item: any, grouping: any): string {
    return this.getGroupingEntity(item, grouping)?.name || '';
  }

  rowSource(item: any, grouping: any): string {
    return this.getGroupingEntity(item, grouping)?.source;
  }

  rowLabels(item: any, grouping: any): any[] {
    return this.getGroupingEntity(item, grouping)?.labels || [];
  }

  subRowName(item: any): string {
    return this.getSubGroupingEntity(item)?.name || '';
  }

  subRowSource(item: any): string {
    return this.getSubGroupingEntity(item)?.source;
  }

  subRowLabels(item: any): any[] {
    return this.getSubGroupingEntity(item)?.labels || [];
  }

  subRowParams(item: any): any {
    return {id: this.getSubGroupingEntity(item)?.id};
  }

  groupingEmptyLabel(grouping: any): string {
    const groupingLabel = grouping?.apiParam ? this.translate.instant(grouping.apiParam) : '';
    return 'No ' + groupingLabel;
  }

  targetingLabel(item: any): string {
    return 'targeting.TARGETING_' + (item?.[this.ctrl?.withGrouping?.apiParam]?.name || '');
  }

  asyncSecondsLabel(): string {
    const seconds = +_.get(this.ctrl, ['asyncQueryStatus', 'secondsInQueue']);
    return seconds === 1 ? 'second' : 'seconds';
  }

  private initializeQuery(): void {
    const config = this.readQueryConfig();
    if (!config.childType) {
      return;
    }

    const childObjectType = OBJECT_TYPE[config.childType];
    if (!childObjectType) {
      return;
    }

    const initKey = [
      config.childType,
      config.parentType || '',
      config.superParentType || '',
      config.useAsyncStats ? '1' : '0'
    ].join('|');
    if (initKey === this.lastInitKey) {
      return;
    }
    this.lastInitKey = initKey;

    this.destroyQuery();

    const parentObjectType = config.parentType ? OBJECT_TYPE[config.parentType] : undefined;
    const superParentObjectType = config.superParentType ? OBJECT_TYPE[config.superParentType] : undefined;

    this.chartType = childObjectType.caps || '';
    this.ctrl = {};
    this.resetPagination();
    this.cleanupQuery = this.adnQueryHelper.setUpQuery(
      this.ctrl,
      childObjectType,
      parentObjectType,
      superParentObjectType,
      config.useAsyncStats ? {useAsyncStats: true} : undefined
    );
  }

  private readQueryConfig(): QueryRouteStateData {
    return this.$state.current?.data || {};
  }

  private destroyQuery(): void {
    if (this.cleanupQuery) {
      this.cleanupQuery();
      this.cleanupQuery = null;
    }
  }

  private sortRows(items: any[], iteratee?: (item: any) => any): any[] {
    const iteratees = _.isArray(iteratee) ? iteratee : [iteratee || _.identity];
    const orders = iteratees.map(() => this.ctrl?.sorting?.asc ? 'asc' : 'desc');
    return _.orderBy(items || [], iteratees, orders);
  }

  // Render only the current page of (already fully-fetched) rows so a large
  // result set doesn't dump thousands of DOM nodes into the table at once.
  private paginate(items: any[]): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return items.slice(start, start + this.pageSize);
  }

  private escapeCsvValue(value: any): string {
    if (_.isNil(value)) {
      return '';
    }

    const stringValue = _.isDate(value) ? value.toISOString() : String(value);
    if (/[",\r\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }
}
